import { artists, contacts, events } from '../../../services/api.admin';
import { fetchTableData, deleteTableData, refresh, resetSelection } from '../tables';
import { fetchDependencies as fetchModalDependencies } from '../modal.forms';
import { v4 as uuidv4 } from 'uuid';
import { MODELS, TEMP_ID_PREFIX } from '../../../constants/admin/settings';
import { apiDataToState, stateToApiData, isArtistRole } from '../../../selectors/admin/models/artists';
import { stateToApiData as userStateToApiData } from '../../../selectors/admin/models/contacts';
import { fetchDependencies, fetchModel, submit, onInput } from '../forms';
import { apiActionCreator, apiMultipleCalls } from '../../helpers';
import { UNPROCESSABLE_ENTITY_CODE } from '../../../constants/http.codes';
import { ADMIN_FORMS, ADMIN_MODAL_FORMS, ADMIN_FLASH } from '../../../constants/actions';
import { lineupAdd } from './lineup';
import { formDataSelector } from '../../../selectors/admin/forms';
import { filter, find } from 'lodash';
import { contextSubjectSelector } from '../../../selectors/admin/context';
import { filtersSelector } from '../../../selectors/admin/tables';
import { VALUE_ALL } from '../../../constants/settings';

export function fetchList(contextId, pagination) {
    return (dispatch, getState) => {
        const filters = filtersSelector(getState(), { tableIndex: 0 });
        const apiCall =
            filters.event && filters.event !== VALUE_ALL
                ? () => artists.getForEvent(filters.event, contextId, pagination)
                : contextId
                  ? () => artists.getOrg(contextId, pagination)
                  : () => artists.get(pagination);
        return fetchTableData(apiCall, 'artists', { pagination })(dispatch, getState);
    };
}

export function fetchArtistsFilterDependencies(organizationId) {
    return fetchDependencies([() => events.getOrgAll(organizationId)], { append: true });
}

export function fetchArtistContactDependencies(organizationId) {
    return fetchDependencies([() => contacts.getOrgAll(organizationId)], {
        append: true
    });
}

export function deleteArtists() {
    return deleteTableData(artists.delete, MODELS.ARTIST);
}

export function create(data) {
    return submit(() => artists.create(stateToApiData(data)), 'Artist successfully created.');
}

export function update(data, id, _, onSuccess) {
    return (dispatch, getState) => {
        const context = contextSubjectSelector(getState());
        return submit(() => artists.update(id, stateToApiData(data), context), 'Artist successfully updated.', {
            beforeDone: (_r, _d, _gs, doneFunction) => {
                onSuccess();
                doneFunction();
            }
        })(dispatch, getState);
    };
}

export function fetchMember(id) {
    return (dispatch, getState) => {
        const context = contextSubjectSelector(getState());
        return fetchModel(() => artists.getMember(id, context), MODELS.ARTIST, {
            transform: apiDataToState
        })(dispatch);
    };
}

export function fetchArtistUsers(subContextId, pagination, contextId, afterDone = () => {}, all = false) {
    return fetchTableData(
        contextId
            ? () => artists[all ? 'getAllOrgUsers' : 'getOrgUsers'](subContextId, contextId, pagination)
            : () => artists[all ? 'getAllUsers' : 'getUsers'](subContextId, pagination),
        'users',
        {
            pagination,
            afterDone
        }
    );
}

export function fetchBulkSetEventsDependencies(organizationId) {
    return fetchModalDependencies([() => events.getOrgAll(organizationId)]);
}

export function bulkUpdateEvents(data, artistIds) {
    return async (dispatch, getState) => {
        dispatch({ type: ADMIN_MODAL_FORMS.SUBMIT });
        try {
            const context = contextSubjectSelector(getState());
            const artistResponses = await Promise.all(artistIds.map(id => artists.getMember(id, context)));
            const artistData = artistResponses.map(r => r.json || r);
            await Promise.all(
                artistData.map(artist =>
                    artists.update(artist.id, stateToApiData({ ...artist, eventIds: data.eventIds || [] }), context)
                )
            );
            dispatch({ type: ADMIN_MODAL_FORMS.SUCCESS });
            dispatch(refresh());
            dispatch(resetSelection(0));
            dispatch({
                type: ADMIN_FLASH.OPEN,
                message: `Events updated for ${artistIds.length} artist${artistIds.length === 1 ? '' : 's'}.`
            });
        } catch (error) {
            dispatch({ type: ADMIN_MODAL_FORMS.FAILED, error: error.message });
        }
    };
}

export function createArtistWithContacts(data, contacts, doneFunction, errorFunction) {
    return (dispatch, getState) => {
        const event = formDataSelector(getState(), { model: MODELS.EVENT });
        if (data.id) {
            apiActionCreator(
                dispatch,
                () => artists.getMember(data.id),
                resp => {
                    const artistResponse = apiDataToState(resp);
                    return apiActionCreator(
                        dispatch,
                        () =>
                            artists.update(
                                data.id,
                                stateToApiData({
                                    ...artistResponse,
                                    organizationIds: [...artistResponse.organizationIds, event.organizationId],
                                    eventIds: [...artistResponse.eventIds, event.id]
                                })
                            ),
                        () => {
                            _createArtistContacts(
                                data.id,
                                contacts,
                                event,
                                doneFunction,
                                dispatch,
                                getState,
                                errorFunction
                            );
                        }
                    );
                }
            );
        } else {
            apiActionCreator(
                dispatch,
                () =>
                    artists.create(
                        stateToApiData({ ...data, organizationIds: [event.organizationId], eventIds: [event.id] }, true)
                    ),
                resp => {
                    dispatch(lineupAdd('artists', { id: resp.id, name: data.name }));
                    dispatch(onInput(MODELS.ENTRY, 'artistId', resp.id));
                    _createArtistContacts(resp.id, contacts, event, doneFunction, dispatch, getState, errorFunction);
                },
                {
                    [UNPROCESSABLE_ENTITY_CODE]: () => {
                        dispatch({
                            type: ADMIN_FORMS.FAILED,
                            errors: { name: 'Artist with this name already exists!' }
                        });
                        errorFunction();
                    }
                }
            );
        }
    };
}

const _newRoleObject = (c, event, artistId) => ({
    artistId,
    function: c.type,
    id: uuidv4(),
    name: c.permission,
    orgId: event.organizationId,
    eventId: event.id,
    accessToClearanceContracts: c.accessToClearanceContracts,
    accessToPerformanceContracts: c.accessToPerformanceContracts
});

function _parseContactErrors(json) {
    if (json && Array.isArray(json.message)) {
        return json.message.reduce((acc, m) => {
            const msg =
                m.param === 'email' && m.value && m.msg === 'user already exists'
                    ? `user with ${m.value} already exists`
                    : m.value
                      ? `${m.msg} (${m.value})`
                      : m.msg;
            return { ...acc, [m.param]: msg };
        }, {});
    }
    return { email: json?.message || 'Validation failed' };
}

function _createArtistContacts(artistId, users, event, doneFunction, dispatch, getState, errorFunction) {
    const contactsToCreate = filter(users, c => c.id.includes(TEMP_ID_PREFIX));
    const contactsToAddPermissions = filter(users, c => !c.id.includes(TEMP_ID_PREFIX));
    const contactErrorActions = errorFunction
        ? {
              [UNPROCESSABLE_ENTITY_CODE]: json => {
                  dispatch({
                      type: ADMIN_FORMS.FAILED,
                      errors: _parseContactErrors(json)
                  });
                  errorFunction();
              }
          }
        : {};
    return apiMultipleCalls(
        dispatch,
        contactsToCreate.map(
            c => () =>
                contacts.create(
                    userStateToApiData({
                        email: c.email,
                        fullName: c.fullName,
                        roles: [_newRoleObject(c, event, artistId)]
                    })
                )
        ),
        () => {
            return apiMultipleCalls(
                dispatch,
                contactsToAddPermissions.map(
                    c => () =>
                        contacts.update(
                            c.id,
                            userStateToApiData({
                                ...c,
                                roles: [
                                    ...c.roles,
                                    ...(find(c.roles, r => isArtistRole(r, artistId, event.organizationId, event.id))
                                        ? []
                                        : [_newRoleObject(c, event, artistId)])
                                ].map(r =>
                                    isArtistRole(r, artistId, event.organizationId, event.id)
                                        ? {
                                              ...r,
                                              accessToPerformanceContracts: c.accessToPerformanceContracts,
                                              accessToClearanceContracts: c.accessToClearanceContracts
                                          }
                                        : { ...r }
                                )
                            }),
                            event.Organization.id
                        )
                ),
                doneFunction
            );
        },
        0,
        {},
        false,
        contactErrorActions
    );
}
